/**
 * REST client for the MCP gateway org-scoped endpoints.
 *
 * Uses the shared apiClient (axios) instance from @/lib/api/client which
 * already handles auth headers, token refresh, and base URL.
 */

import apiClient from '@/lib/api/client';
import type {
  BaseTool,
  BuildFromSourcePayload,
  BuildStatus,
  CustomTool,
  McpPrompt,
  ToolMapping,
  ToolSecretKey,
  VirtualServer,
  VirtualServerCreate,
  VirtualServerUpdate,
} from './types';
import type {
  CatalogEntry,
  CreateConnectionRequest,
  CreateConnectionResponse,
  MCPConnection,
  PlatformMcp,
  PlatformMcpEnablement,
  TestConnectionResponse,
  UpdateConnectionRequest,
} from '@/features/integrations/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const orgPath = (orgId: string) => `/v1/organizations/${orgId}`;

// ---------------------------------------------------------------------------
// Base tools
// ---------------------------------------------------------------------------

async function listBaseTools(orgId: string): Promise<BaseTool[]> {
  const { data } = await apiClient.get<BaseTool[]>(`${orgPath(orgId)}/tools/base`);
  return data;
}

async function listEnabledToolIds(orgId: string): Promise<string[]> {
  const { data } = await apiClient.get<{ enabled_tool_ids: string[] }>(
    `${orgPath(orgId)}/enablement`,
  );
  return data.enabled_tool_ids ?? [];
}

async function getTool(orgId: string, toolId: string): Promise<BaseTool | CustomTool> {
  const { data } = await apiClient.get<BaseTool | CustomTool>(
    `${orgPath(orgId)}/tools/${toolId}`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Enablement
// ---------------------------------------------------------------------------

async function enableTool(
  orgId: string,
  toolId: string,
  toolType: 'base' | 'custom' = 'base',
): Promise<void> {
  await apiClient.post(`${orgPath(orgId)}/enablement/${toolId}/enable`, {
    tool_type: toolType,
  });
}

async function disableTool(orgId: string, toolId: string): Promise<void> {
  await apiClient.post(`${orgPath(orgId)}/enablement/${toolId}/disable`, {});
}

// ---------------------------------------------------------------------------
// Custom tools
// ---------------------------------------------------------------------------

async function listCustomTools(orgId: string): Promise<CustomTool[]> {
  const { data } = await apiClient.get<CustomTool[]>(
    `${orgPath(orgId)}/custom-tools`,
  );
  return data;
}

async function createCustomTool(
  orgId: string,
  payload: Partial<CustomTool>,
): Promise<CustomTool> {
  const { data } = await apiClient.post<CustomTool>(
    `${orgPath(orgId)}/custom-tools`,
    payload,
  );
  return data;
}

async function updateCustomTool(
  orgId: string,
  toolId: string,
  payload: Partial<CustomTool>,
): Promise<CustomTool> {
  const { data } = await apiClient.put<CustomTool>(
    `${orgPath(orgId)}/custom-tools/${toolId}`,
    payload,
  );
  return data;
}

async function deleteCustomTool(orgId: string, toolId: string): Promise<void> {
  await apiClient.delete(`${orgPath(orgId)}/custom-tools/${toolId}`);
}

async function buildFromSource(
  orgId: string,
  payload: BuildFromSourcePayload,
): Promise<{ build_id: string }> {
  const { data } = await apiClient.post<{ build_id: string }>(
    `${orgPath(orgId)}/custom-tools/source`,
    payload,
    { timeout: 120_000 },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Virtual servers
// ---------------------------------------------------------------------------

async function listVServers(orgId: string): Promise<VirtualServer[]> {
  const { data } = await apiClient.get<VirtualServer[]>(
    `${orgPath(orgId)}/vservers`,
  );
  return data;
}

async function getVServer(
  orgId: string,
  vserverId: string,
): Promise<VirtualServer> {
  const { data } = await apiClient.get<VirtualServer>(
    `${orgPath(orgId)}/vservers/${vserverId}`,
  );
  return data;
}

async function createVServer(
  orgId: string,
  payload: VirtualServerCreate,
): Promise<VirtualServer> {
  const { data } = await apiClient.post<VirtualServer>(
    `${orgPath(orgId)}/vservers`,
    payload,
  );
  return data;
}

async function updateVServer(
  orgId: string,
  vserverId: string,
  payload: VirtualServerUpdate,
): Promise<VirtualServer> {
  const { data } = await apiClient.put<VirtualServer>(
    `${orgPath(orgId)}/vservers/${vserverId}`,
    payload,
  );
  return data;
}

async function deleteVServer(
  orgId: string,
  vserverId: string,
): Promise<void> {
  await apiClient.delete(`${orgPath(orgId)}/vservers/${vserverId}`);
}

async function setToolMappings(
  orgId: string,
  vserverId: string,
  mappings: ToolMapping[],
): Promise<void> {
  await apiClient.put(
    `${orgPath(orgId)}/vservers/${vserverId}/tools`,
    { mappings },
  );
}

async function listToolMappings(
  orgId: string,
  vserverId: string,
): Promise<ToolMapping[]> {
  const { data } = await apiClient.get<ToolMapping[]>(
    `${orgPath(orgId)}/vservers/${vserverId}/tools`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// MCP catalog + connections (Integrations)
// ---------------------------------------------------------------------------

async function listCatalog(): Promise<CatalogEntry[]> {
  const { data } = await apiClient.get<CatalogEntry[]>('/v1/mcp/catalog');
  return data;
}

async function listConnections(orgId: string): Promise<MCPConnection[]> {
  const { data } = await apiClient.get<MCPConnection[]>(`${orgPath(orgId)}/mcp-connections`);
  return data;
}

/**
 * Platform integrations visible to an org, each with the tools it declares —
 * enough to list tool names without calling the running server.
 */
async function listPlatformMcps(orgId: string): Promise<PlatformMcp[]> {
  const { data } = await apiClient.get<PlatformMcp[]>(`${orgPath(orgId)}/platform-mcps`);
  return data;
}

/**
 * A platform integration is only dispatched for an org once it is enabled;
 * without this a virtual server mapping it exposes no tools at all.
 */
async function getPlatformMcpEnablement(
  orgId: string,
  pmcpId: string,
): Promise<PlatformMcpEnablement> {
  const { data } = await apiClient.get<PlatformMcpEnablement>(
    `${orgPath(orgId)}/platform-mcps/${pmcpId}/enablement`,
  );
  return data;
}

async function setPlatformMcpEnablement(
  orgId: string,
  pmcpId: string,
  payload: PlatformMcpEnablement,
): Promise<PlatformMcpEnablement> {
  const { data } = await apiClient.put<PlatformMcpEnablement>(
    `${orgPath(orgId)}/platform-mcps/${pmcpId}/enablement`,
    payload,
  );
  return data;
}

async function getConnection(orgId: string, connId: string): Promise<MCPConnection> {
  const { data } = await apiClient.get<MCPConnection>(`${orgPath(orgId)}/mcp-connections/${connId}`);
  return data;
}

async function createConnection(
  orgId: string,
  payload: CreateConnectionRequest,
): Promise<CreateConnectionResponse> {
  const { data } = await apiClient.post<CreateConnectionResponse>(
    `${orgPath(orgId)}/mcp-connections`,
    payload,
  );
  return data;
}

async function updateConnection(
  orgId: string,
  connId: string,
  payload: UpdateConnectionRequest,
): Promise<MCPConnection> {
  const { data } = await apiClient.put<MCPConnection>(
    `${orgPath(orgId)}/mcp-connections/${connId}`,
    payload,
  );
  return data;
}

async function deleteConnection(orgId: string, connId: string): Promise<void> {
  await apiClient.delete(`${orgPath(orgId)}/mcp-connections/${connId}`);
}

async function testConnection(orgId: string, connId: string): Promise<TestConnectionResponse> {
  const { data } = await apiClient.post<TestConnectionResponse>(
    `${orgPath(orgId)}/mcp-connections/${connId}/test`,
  );
  return data;
}

/**
 * Full-page navigation target for the OAuth authorize dance — not an XHR call.
 * KNOWN GAP: this GET is permission-gated via the same Authorization-header
 * scheme as every other endpoint, but a raw browser navigation cannot carry
 * that header (this platform has no cookie-based auth anywhere). A real
 * click-through will 401 until a redirect-token (or similar) mechanism is
 * added server-side — see WS-10 status.md. Bearer/custom connections are
 * unaffected (pure XHR, real auth header attaches correctly).
 */
function oauthAuthorizeUrl(orgId: string, connId: string): string {
  return `${orgPath(orgId)}/mcp-connections/${connId}/oauth/authorize`;
}

async function revokeOAuth(orgId: string, connId: string): Promise<MCPConnection> {
  const { data } = await apiClient.delete<MCPConnection>(
    `${orgPath(orgId)}/mcp-connections/${connId}/oauth`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Env vars
// ---------------------------------------------------------------------------

async function setEnvVar(
  orgId: string,
  toolId: string,
  key: string,
  value: string,
): Promise<void> {
  await apiClient.post(`${orgPath(orgId)}/tool-env/${toolId}/env`, {
    key,
    value,
  });
}

async function listEnvVars(
  orgId: string,
  toolId: string,
): Promise<Record<string, string>> {
  const { data } = await apiClient.get<Record<string, string>>(
    `${orgPath(orgId)}/tool-env/${toolId}/env`,
  );
  return data;
}

async function deleteEnvVar(
  orgId: string,
  toolId: string,
  key: string,
): Promise<void> {
  await apiClient.delete(
    `${orgPath(orgId)}/tool-env/${toolId}/env/${key}`,
  );
}

// ---------------------------------------------------------------------------
// Secrets
// ---------------------------------------------------------------------------

async function setSecret(
  orgId: string,
  toolId: string,
  key: string,
  value: string,
): Promise<void> {
  await apiClient.post(`${orgPath(orgId)}/tool-env/${toolId}/secrets`, {
    key,
    value,
  });
}

async function listSecrets(
  orgId: string,
  toolId: string,
): Promise<ToolSecretKey[]> {
  const { data } = await apiClient.get<ToolSecretKey[]>(
    `${orgPath(orgId)}/tool-env/${toolId}/secrets`,
  );
  return data;
}

async function deleteSecret(
  orgId: string,
  toolId: string,
  key: string,
): Promise<void> {
  await apiClient.delete(
    `${orgPath(orgId)}/tool-env/${toolId}/secrets/${key}`,
  );
}

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

async function createPrompt(
  orgId: string,
  payload: Partial<McpPrompt> & { vserver_id: string },
): Promise<McpPrompt> {
  const { data } = await apiClient.post<McpPrompt>(
    `${orgPath(orgId)}/prompts`,
    payload,
  );
  return data;
}

async function listPrompts(
  orgId: string,
  vserverId: string,
): Promise<McpPrompt[]> {
  const { data } = await apiClient.get<McpPrompt[]>(
    `${orgPath(orgId)}/prompts/${vserverId}`,
  );
  return data;
}

async function updatePrompt(
  orgId: string,
  name: string,
  payload: Partial<McpPrompt>,
): Promise<McpPrompt> {
  const { data } = await apiClient.put<McpPrompt>(
    `${orgPath(orgId)}/prompts/${name}`,
    payload,
  );
  return data;
}

async function deletePrompt(
  orgId: string,
  name: string,
  vserverId: string,
): Promise<void> {
  await apiClient.delete(`${orgPath(orgId)}/prompts/${name}`, {
    params: { vserver_id: vserverId },
  });
}

// ---------------------------------------------------------------------------
// Builds
// ---------------------------------------------------------------------------

async function listBuilds(
  orgId: string,
  toolId: string,
): Promise<BuildStatus[]> {
  const { data } = await apiClient.get<BuildStatus[]>(
    `${orgPath(orgId)}/custom-tools/${toolId}/builds`,
  );
  return data;
}

async function getBuild(
  orgId: string,
  toolId: string,
  buildId: string,
): Promise<BuildStatus> {
  const { data } = await apiClient.get<BuildStatus>(
    `${orgPath(orgId)}/custom-tools/${toolId}/builds/${buildId}`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const mcpRestClient = {
  // Base tools
  listBaseTools,
  getTool,
  listEnabledToolIds,
  // Enablement
  enableTool,
  disableTool,
  // Custom tools
  listCustomTools,
  createCustomTool,
  updateCustomTool,
  deleteCustomTool,
  buildFromSource,
  // Virtual servers
  listVServers,
  getVServer,
  createVServer,
  updateVServer,
  deleteVServer,
  setToolMappings,
  listToolMappings,
  // MCP catalog + connections (Integrations)
  listCatalog,
  listPlatformMcps,
  getPlatformMcpEnablement,
  setPlatformMcpEnablement,
  listConnections,
  getConnection,
  createConnection,
  updateConnection,
  deleteConnection,
  testConnection,
  oauthAuthorizeUrl,
  revokeOAuth,
  // Env vars
  setEnvVar,
  listEnvVars,
  deleteEnvVar,
  // Secrets
  setSecret,
  listSecrets,
  deleteSecret,
  // Prompts
  createPrompt,
  listPrompts,
  updatePrompt,
  deletePrompt,
  // Builds
  listBuilds,
  getBuild,
};
